import { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { api } from "../../lib/api";
import { Upload, X, Loader2, Check } from "lucide-react";

/**
 * ImageUpload
 * -----------------------------------------------------------------------
 * Generic CMS image uploader with optional in-browser crop step.
 *
 *  - value / onChange: bound to a URL string in parent state.
 *  - aspect:        display ratio of the preview thumbnail (video | square | logo).
 *  - cropAspect:    when set (e.g. 16/9), force a crop modal before upload
 *                   so cards look uniform regardless of source aspect.
 */
export default function ImageUpload({
  value,
  onChange,
  label = "Imagen",
  help,
  aspect = "video",
  cropAspect,
  accept = "image/*",
  focalX,
  focalY,
  onFocalChange,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [cropSrc, setCropSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState(null);

  const onCropComplete = useCallback((_, areaPixels) => setCroppedArea(areaPixels), []);

  const doUpload = async (blob, filename) => {
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, filename);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Save the RELATIVE URL (`/api/files/...`) instead of prepending the
      // current backend origin. Absolute URLs bake in whichever environment
      // uploaded the image (preview vs prod), breaking hero images after
      // publishing. Relative URLs resolve against whatever domain serves the
      // page, so the same DB works in both environments.
      onChange(data.url);
    } catch (e) {
      setError(e.response?.data?.detail || "Error al subir");
    } finally {
      setUploading(false);
    }
  };

  const handleFile = (file) => {
    if (!file) return;
    if (cropAspect) {
      // Open crop modal
      const reader = new FileReader();
      reader.onload = () => {
        setCropSrc(reader.result);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setError("");
      };
      reader.readAsDataURL(file);
    } else {
      doUpload(file, file.name);
    }
  };

  const confirmCrop = async () => {
    if (!cropSrc || !croppedArea) return;
    setUploading(true);
    try {
      const blob = await getCroppedBlob(cropSrc, croppedArea, cropAspect);
      await doUpload(blob, "cover-16x9.jpg");
      setCropSrc(null);
    } catch (e) {
      setError(e.message || "Error al recortar");
      setUploading(false);
    }
  };

  const onFileInput = (e) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const ratioCls =
    aspect === "square" ? "aspect-square" : aspect === "logo" ? "aspect-[3/2]" : "aspect-video";

  return (
    <div data-testid="image-upload">
      {label && <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">{label}</label>}

      {value ? (
        <>
          <div className="relative bg-gray-50 border border-gray-200 p-3 flex items-center gap-4">
            <div className={`${ratioCls} w-40 bg-white border border-gray-100 flex items-center justify-center overflow-hidden shrink-0`}>
              <img
                src={value}
                alt=""
                className="w-full h-full object-cover"
                style={onFocalChange ? { objectPosition: `${focalX ?? 50}% ${focalY ?? 50}%` } : undefined}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 truncate">{value}</p>
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  className="text-xs px-3 py-1.5 border border-gray-200 hover:border-[#0F2040] inline-flex items-center gap-1.5"
                  data-testid="upload-replace"
                >
                  <Upload className="w-3 h-3" /> Reemplazar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onChange("");
                    onFocalChange?.({ focal_x: 50, focal_y: 50 });
                  }}
                  className="text-xs px-3 py-1.5 border border-gray-200 hover:border-red-500 hover:text-red-600 inline-flex items-center gap-1.5"
                  data-testid="upload-clear"
                >
                  <X className="w-3 h-3" /> Quitar
                </button>
              </div>
            </div>
          </div>

          {onFocalChange && (
            <div className="mt-3 grid md:grid-cols-2 gap-4 bg-gray-50 border border-gray-200 p-4" data-testid="focal-block">
              <div>
                <p className="text-xs font-medium text-gray-700 mb-2 uppercase tracking-wider">Punto focal</p>
                <div
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
                    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
                    onFocalChange({ focal_x: Math.round(x), focal_y: Math.round(y) });
                  }}
                  data-testid="focal-picker"
                  className="relative w-full aspect-video bg-white border border-gray-200 cursor-crosshair overflow-hidden select-none"
                  title="Haga clic sobre el punto que debe quedar centrado"
                >
                  <img src={value} alt="" className="w-full h-full object-contain" draggable={false} />
                  <div
                    className="absolute w-6 h-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-lg bg-[#C8102E] pointer-events-none"
                    style={{ left: `${focalX ?? 50}%`, top: `${focalY ?? 50}%` }}
                    data-testid="focal-marker"
                  />
                </div>
                <p className="text-[11px] text-gray-500 mt-2">
                  Coordenadas: {focalX ?? 50}% × {focalY ?? 50}%
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-700 mb-2 uppercase tracking-wider">Vista previa pública</p>
                <div className={`w-full ${ratioCls} bg-gray-100 overflow-hidden border border-gray-200`}>
                  <img
                    src={value}
                    alt=""
                    className="w-full h-full object-cover"
                    style={{ objectPosition: `${focalX ?? 50}% ${focalY ?? 50}%` }}
                  />
                </div>
                <p className="text-[11px] text-gray-500 mt-2">Así se verá en la portada.</p>
              </div>
            </div>
          )}
        </>
      ) : (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 hover:border-[#0F2040] cursor-pointer p-8 text-center transition-colors"
          data-testid="upload-dropzone"
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 text-gray-400 mx-auto animate-spin" />
          ) : (
            <>
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" strokeWidth={1.4} />
              <p className="text-sm font-medium text-[#0F2040]">Arrastre el archivo aquí o haga clic para seleccionar</p>
              <p className="text-xs text-gray-500 mt-1">
                JPG, PNG, WebP, GIF o SVG · máx. 5 MB
                {cropAspect ? " · se recortará automáticamente" : ""}
              </p>
            </>
          )}
        </div>
      )}

      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={onFileInput} data-testid="upload-input" />

      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      {help && <p className="text-xs text-gray-500 mt-2">{help}</p>}

      {/* ---------- Optional URL fallback ---------- */}
      <details className="mt-2">
        <summary className="text-xs text-gray-500 cursor-pointer select-none">¿Prefiere pegar una URL?</summary>
        <input
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          className="w-full mt-2 px-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm"
        />
      </details>

      {/* ---------- Crop modal ---------- */}
      {cropSrc && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" data-testid="crop-modal">
          <div className="bg-white max-w-2xl w-full">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-[#0F2040]">
                Recortar imagen · {cropAspect === 16 / 9 ? "16:9" : cropAspect === 1 ? "1:1" : "personalizado"}
              </h3>
              <button type="button" onClick={() => setCropSrc(null)} disabled={uploading}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="relative w-full h-[420px] bg-black">
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                aspect={cropAspect}
                showGrid
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="px-6 py-4 space-y-3 border-t border-gray-100">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1 uppercase tracking-wider">Zoom</label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="w-full accent-[#C8102E]"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setCropSrc(null)}
                  disabled={uploading}
                  className="px-4 py-2 text-sm border border-gray-200 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmCrop}
                  disabled={uploading}
                  data-testid="crop-confirm"
                  className="bg-[#C8102E] text-white px-4 py-2 text-sm flex items-center gap-2 hover:bg-[#A30D25] disabled:opacity-60"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Recortar y subir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

async function getCroppedBlob(src, area, aspect) {
  const image = await loadImage(src);
  // Target width up to 1600, height derived from aspect
  const targetW = Math.min(1600, Math.round(area.width));
  const targetH = aspect ? Math.round(targetW / aspect) : Math.round(area.height);
  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, targetW, targetH);
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Canvas empty"))), "image/jpeg", 0.86)
  );
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
